import dayjs from 'dayjs'
import type { IFilterParams as IListFilterParams } from './useExperienceList'
import { attrRankController } from '~/apis/back/services'
import type {
  AttSpecialTopicConfigGroupDetailResponseDTO,
  AttSpecialTopicConfigImagesDTO,
  ResponseBaseSingleResponseDTOAttSpecialTopicConfigDetailResponseDTO,
} from '~/apis/back/types'
import type { BookItemClickEvent, ViewItemViewEvent } from '~/types/event-tracking'
import { ENUM_EventType, ENUM_PageName } from '~/enums'
import type { ISKU, ISKUItem, ISKUPrice } from '~/apis/back/types/SKU/SKU'

interface ISupplierItem {
  id: number
  price?: number
  logo: string
  cashbackDesc: string
  showAll: boolean
  showMore: boolean
  details: Array<AttSpecialTopicConfigGroupDetailResponseDTO>
}

interface IFilterParams {
  travelDate: string
  freeCancellation?: boolean
  instantConfirmation?: boolean
}

export function useExperienceDetail() {
  const route = useRoute()
  const { t } = useI18n()
  const userStore = useUserStore()
  const isMobile = useMobileByScreen()
  const { filterConditions } = useFilterConditionsStore()
  const { locale } = useLocale()
  const gtm = useGtm()

  const cleanDescription = (html: string): string => {
    if (!html) return '';
    
    // First remove common HTML tags specifically
    let text = html
      .replace(/<br\s*\/?>/gi, '\n') // Convert <br> to newlines
      .replace(/<p\b[^>]*>(.*?)<\/p>/gi, '$1\n\n') // Convert paragraphs to text with double newlines
      .replace(/<div\b[^>]*>(.*?)<\/div>/gi, '$1\n') // Convert divs to text with newlines
      .replace(/<li\b[^>]*>(.*?)<\/li>/gi, '• $1\n') // Convert list items to bullet points
      .replace(/<[^>]*>/g, ''); // Remove any remaining tags
    
    // Handle HTML entities
    text = text
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    
    return text.trim();
  }

  const filterParams = useState<IFilterParams>('azgo_experience_detail_filter', () => ({
    travelDate: route.query.travelDate as string || dayjs().format('YYYY-MM-DD'),
    freeCancellation: undefined,
    instantConfirmation: undefined,
  }))

  const experienceState = useState('azgo_experience_detail', () => {
    return {
      experienceDetail: {} as ResponseBaseSingleResponseDTOAttSpecialTopicConfigDetailResponseDTO,
      isError: false,
      errorMessage: '',
      defaultExpandLength: 5,
      currentSku: null as AttSpecialTopicConfigGroupDetailResponseDTO | null,
      skuDescVisible: false,
      supplierList: [] as ISupplierItem[],
    }
  })

  const {
    experienceDetail,
    isError,
    errorMessage,
    defaultExpandLength,
    supplierList,
    currentSku,
    skuDescVisible,
  } = toRefs(experienceState.value)

  const experienceInfo = computed(() => experienceDetail.value.data?.data)

  function watchExperienceDetail() {
    const supplierData = experienceDetail.value.data?.data?.specialTopicDetails
    if (!supplierData?.length) {
      supplierList.value = []
      return
    }
    supplierList.value = supplierData
      .sort((a, b) => {
        const aPrice = a.price || Infinity
        const bPrice = b.price || Infinity
        return aPrice - bPrice
      })
      .reduce((acc: ISupplierItem[], item) => {
        const supplierItem = acc.find(accItem => accItem.id === item.supplierId)
        if (supplierItem) {
          // 保证不重复
          if (!supplierItem.details.find(c => c.productCode === item.productCode)) {
            supplierItem.details.push(item)
          }
          return acc
        }
        return [...acc, {
          id: item.supplierId!,
          price: item.price,
          logo: item.supplierImageUrl!,
          cashbackDesc: experienceDetail.value.data?.data?.cashbackDesc || '',
          showAll: false,
          showMore: false,
          details: [item],
        }]
      }, [] as ISupplierItem[])
    supplierList.value.forEach((item) => {
      if (item.details.length > defaultExpandLength.value) {
        item.showAll = false
        item.showMore = true
      }
      else {
        item.showAll = true
        item.showMore = false
      }
    })
  }

  async function getExperienceDetail() { // productCode: string, skuItem:string
    const { productSKU } = route.params as {
      productSKU: string
    };
    
    // Split the string into an array based on '-'
    const parts = productSKU.split('-');
    
    // Get the last element of the array
    const productCode:string = parts[parts.length - 2];
    const attProductSkuPkId:string =  parts[parts.length - 1];
    // const combinedProductAndSKU:string = parts[0:parts.length-1]

    // console.log("productCode", productCode)
    // console.log("attProductSkuPkId", attProductSkuPkId)

    // const { travelDate, instantConfirmation, freeCancellation } = filterParams.value
    const { data, error } = await attrRankController.rankGetSpecialTopicSKUConfigDetailPost({
      productCode: productCode //"3695CTC"
      // specialTopicSerialNo: serialNo,
      // travelDate,
      // instantConfirmation: instantConfirmation ? 1 : undefined,
      // freeCancellation: freeCancellation ? 1 : undefined,
    });

    // console.log("data", data)


    if (error.value || !data.value || !data.value.data?.success || !data?.value?.data?.data) {
      experienceState.value.isError = true;
      experienceState.value.errorMessage = data.value?.data?.errorMessage || '获取数据失败';
      return
    }
    const forbiddenSuppliedId: string[] = ['12', '21', '22', '17', '25']
    // I am ignoring the following like because I am 100% sure the type is ISKU
    // I would need to rewrite 5 backend layers to make it an array of ISKU[]
    // @ts-expect-error - Ignore TypeScript type checking for this line
    const currentProductData: ISKU = data?.value?.data?.data.filter(element => !forbiddenSuppliedId.includes(element.supplierId))[0];




    // console.log("currentProductData", currentProductData)
    const currentSKUData: ISKUItem = currentProductData?.skuList?.filter((element:ISKUItem)  => element?.productSku?.includes(attProductSkuPkId))[0] ?? {};

    // console.log("currentSKUData", currentSKUData)

    // We are finding the price.
    const currentSKUPrice:number|undefined  = currentSKUData?.prices?.filter((element:ISKUPrice)  => element?.productSku?.includes(attProductSkuPkId))[0]?.price ?? undefined

    

    // If there is no image with cover=TRUE, we just take the first image of the array.

    const coverImageURL: string = currentSKUData?.product?.imageUrls?.filter(element => element?.isCover === true)[0]?.url ?? ""
    const allNonCoverImages: AttSpecialTopicConfigImagesDTO[] = 
    currentSKUData?.product?.imageUrls
      ?.filter(element => element.url && (element?.isCover === false || element?.isCover === undefined))
      .map(element => ({
        imageUrl: element.url
        // Add other fields if necessary
      })) || [];
    let specialTopicName:string  = ""

    // For suppplied ID 3, we only take the product_title and not product_title + sku_title.
    if (currentProductData.supplierId === "3") {
      specialTopicName = currentProductData?.title ?? ""
    } else {
      specialTopicName = currentProductData?.title + " " + (currentSKUData?.skuTitle ?? "")
    }


    // We are finding the logo of a supplier.
    const supplierImages: Record<number | string, string> = {
      4: "https://file.azgo-static.com/ibu-common/f41b6a75abd35ee64a1f166366627335.png",     // KKday
      3: "https://file.azgo-static.com/ibu-common/277a923be2de07c9d377ac7f82b6f21e.png",     // Trip.com
      7: "https://file.azgo-static.com/ibu-common/73296e59b5758aeaf63c22ac529d4d04.png",     // GetYourGuide
      16: "https://file.azgo-static.com/ibu-common/56fd555a96ac0b8bdf59651d51c246c2.png",    // Headout
      14: "https://file.azgo-static.com/ibu-common/6e05db64f5c0e9766307936644fbc7d7.png",    // Fever
      6: "https://file.azgo-static.com/ibu-common/f1aec07a20f361a40793aaf3a1a1e293.png",     // Tiqets
      19: "https://file.azgo-static.com/ibu-common/d2ad01d29b0b125adc8d57a56e0c9867.png", // Klook (as a string due to special format)
      26: "https://file.azgo-static.com/ibu-common/d2ad01d29b0b125adc8d57a56e0c9867.png", // Klook (as a string due to special format)
      55: "https://file.azgo-static.com/ibu-common/0f8996f82bfc0a913bc328aacd36b63d.png",    // Viator
      13: "", // Musement - Assuming no URL was provided; replace with actual URL if needed
    };

    let supplierImageURL
    if  (Number(currentProductData?.supplierId) in supplierImages) {
      supplierImageURL = supplierImages[Number(currentProductData?.supplierId)]
    }


    const specialTopicDetails: AttSpecialTopicConfigGroupDetailResponseDTO[] = [{
      specialTopicSerialNo: currentProductData.productCode,
      supplierId: Number(currentProductData?.supplierId),
      supplierImageUrl: supplierImageURL,
      productCode: currentProductData.productCode,
      productTitle: specialTopicName,// "jssjs",// currentProductData.attTitle,
      skuCode: currentSKUData.attProductSkuPkId,
      skuTitle: currentSKUData.skuTitle,
      countryId: Number(currentProductData.supplierCountryId),
      countryName: currentProductData.supplierCountryName,
      azgoCountryId: Number(currentProductData.supplierCountryId),
      azgoCountryName: currentProductData.supplierCountryName,
      cityId: Number(currentProductData.supplierCityId),
      cityName: currentProductData.supplierCityName,
      azgoCityId: Number(currentProductData.supplierCityId),
      azgoCityName: currentProductData.supplierCityName,
      productUrl: currentSKUData?.product?.url,
      // reviews: currentSKUData,
      // rating: number,
      // tag: string,
      // cancellationPolicy: string,
      // confirmPolicy: string,
      // ageBand: string,
      // ticketType: string,
      // ext: string,
      // freeCancellation: number,
      // instantConfirmation: number,
      // productDescription: string,
      // skuDescription: string,
      price: currentSKUPrice,
      // sort: currentSKUData,
      // attSourcePkId: number,
    }]

    const experienceDetailData: ResponseBaseSingleResponseDTOAttSpecialTopicConfigDetailResponseDTO = {
      errorCode: "",
      errorMessage: "",
      data :
      {
          data:  {
            specialTopicSerialNo:"",
            countryId: Number(currentProductData.supplierCountryId),
            countryName: currentProductData.supplierCountryName,
            cityId: Number(currentProductData.supplierCityId),
            cityName: currentProductData.supplierCityName,
            specialTopicName: specialTopicName ,
            specialTopicType: 1,
            cover: coverImageURL,
            specialTopicDetails: specialTopicDetails,
            intro: cleanDescription(currentProductData?.description ?? ''),
            cashbackDesc: "",
            images: allNonCoverImages,
        }
      }
    };

    experienceState.value.experienceDetail =  experienceDetailData //data.value
    watchExperienceDetail()
    return experienceState
  }

  function trackDetailViewDealEvent(isBigBtn = false, jumpUrl: string, items: ISupplierItem[], supplierRankIndex: number, proRankIndex: number) {
    const skuItem = items[supplierRankIndex] || {}

    const rowData = (isBigBtn ? skuItem?.details?.[0] : skuItem?.details?.[proRankIndex]) || {}

    const selectItemParams: BookItemClickEvent = {
      event: ENUM_EventType.BookItem,
      page_category: ENUM_PageName.Detail,
      city_id: experienceInfo.value?.cityId,
      city: experienceInfo.value?.cityName,
      country_code: experienceInfo.value?.countryId, // 后端 没返回 country_code 例如 86 这样的
      locale: locale.value,
      search_date: dayjs(filterConditions.date).format('YYYY-MM-DD'),
      campaign_id: route.query.campaign_id as string ?? '',
      campaign_name: route.query.campaign_name as string ?? '',
      currency: userStore.userInfo.currency,
      supplier_id: rowData.supplierId,
      supplier_name: '', // 没找到字段
      supplier_list_position: supplierRankIndex + 1,
      product_id: rowData.productCode,
      product_name: rowData.productTitle,
      product_list_position: !isBigBtn ? (proRankIndex ?? 0) + 1 : 0,
      product_price: rowData?.price,
      click_url: jumpUrl,
      button_type: isBigBtn ? 'fromprice_button' : 'product_button',
      experience_id: experienceInfo.value?.specialTopicSerialNo,
      experience_name: experienceInfo.value?.specialTopicName,

    }
    gtm?.trackEvent(selectItemParams)
  }
  async function viewDeal(item: AttSpecialTopicConfigGroupDetailResponseDTO, supplierRankIndex: number, proRankIndex: number, isBigBtn: boolean) {
    const { query } = route
    const { userInfo } = userStore
    const { data } = await attrRankController.rankGetExperiencesJumpUrlPost({
      supplierId: item?.supplierId,
      path: item.productUrl,
      jumpSnapDTO: {
        cid: query.cid as string,
        memberId: userInfo.memberId,
      },
    })
    const jumpUrl = data.value?.data?.data?.jumpUrl
    if (jumpUrl) {
      trackDetailViewDealEvent(isBigBtn, jumpUrl, supplierList.value, supplierRankIndex, proRankIndex)
      //  打开新窗口
      return navigateTo(data.value?.data?.data?.jumpUrl, {
        external: true,
        open: {
          target: isMobile.value ? '_self' : '_blank',
        },
      })
    }
    else {
      showToast(t('key.price.calendar.request.failed'))
    }
  }

  function showMoreHandler(item: ISupplierItem) {
    if (item.details.length > defaultExpandLength.value) {
      item.showAll = false
      item.showMore = true
    }
    else {
      item.showAll = true
      item.showMore = false
    }
  }

  function showAllHandler(item: ISupplierItem) {
    item.showAll = true
    item.showMore = false
  }

  function showFewHandler(item: ISupplierItem) {
    item.showAll = false
    item.showMore = false
  }

  function showLessOrShowAll(item: ISupplierItem) {
    if (item.showAll) {
      showFewHandler(item)
    }
    else {
      showAllHandler(item)
    }
  }

  function getPrice(price?: number) {
    if (!price) {
      return t('key.attraction.detail.price.getPrice')
    }
    return getCurrencySymbol(userStore.userInfo.currency) + toThousands(price)
  }

  function showSkuDesc(item: AttSpecialTopicConfigGroupDetailResponseDTO) {
    currentSku.value = item
    skuDescVisible.value = true
  }

  function initFilter() {
    const filterCookie = useCookie<IListFilterParams>('azgo-experience-filter')
    filterParams.value.freeCancellation = !!filterCookie.value?.freeCancellation
    filterParams.value.instantConfirmation = !!filterCookie.value?.instantConfirmation
  }

  function trackViewItem() {
    const params: ViewItemViewEvent = {
      event: ENUM_EventType.ViewItem,
      page_category: ENUM_PageName.Detail,
      city_id: experienceInfo.value?.cityId,
      city: experienceInfo.value?.cityName,
      locale: locale.value,
      campaign_id: route.query.campaign_id as string ?? '',
      campaign_name: route.query.campaign_name as string ?? '',
      prd_type: 'Experiences',
      supplier_number: supplierList.value?.length ?? 0,
      experience_id: experienceInfo.value?.specialTopicSerialNo,
      experience_name: experienceInfo.value?.specialTopicName,
    }
    gtm?.trackEvent(params)
  }
  return {
    experienceState,
    experienceDetail,
    isError,
    errorMessage,
    experienceInfo,
    supplierList,
    defaultExpandLength,
    currentSku,
    skuDescVisible,
    filterParams,
    initFilter,
    getExperienceDetail,
    viewDeal,
    showMoreHandler,
    showLessOrShowAll,
    getPrice,
    showSkuDesc,
    trackViewItem,
  }
}
